import { useCallback, useEffect, useState } from 'react';
import { api, openAuthenticatedFile } from '../api/client.js';
import type { Paginated, QuoteListEntry } from '../api/types.js';

/**
 * Kundenhistorie.
 *
 * Zeigt ausschliesslich die Angebote des eigenen Kontos -- die Abgrenzung erfolgt
 * serverseitig ueber den Kontokontext, nicht durch Filtern in dieser Ansicht.
 */
export function QuoteHistory({ nonce }: { nonce: number }): React.JSX.Element {
  const [items, setItems] = useState<readonly QuoteListEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const r = await api.get<Paginated<QuoteListEntry>>('/api/quotes?limit=25');
      setItems(r.items);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden, nonce]);

  return (
    <section className="card">
      <h3>Meine Angebote</h3>
      {error !== null && <p role="alert">{error}</p>}
      {items.length === 0 && <p className="muted">Noch keine Angebote.</p>}

      {items.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Angebotsnummer</th>
                <th scope="col">Datum</th>
                <th scope="col">Status</th>
                <th scope="col">Positionen</th>
                <th scope="col">Gesamt netto</th>
                <th scope="col">PDF</th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.quoteId}>
                  <th scope="row">
                    <code>{q.quoteNumber ?? '–'}</code>
                  </th>
                  <td>{q.quoteDate ?? '–'}</td>
                  <td>{q.status === 'issued' ? 'ausgestellt' : q.status}</td>
                  <td className="num">{q.itemCount}</td>
                  <td className="num">
                    {q.documentTotalNet === null ? '–' : `${q.documentTotalNet} €`}
                  </td>
                  <td>
                    {q.pdfAvailable ? (
                      <button
                        type="button"
                        className="link"
                        onClick={() => {
                          void openAuthenticatedFile(
                            `/api/quotes/${q.quoteId}/pdf`,
                          ).catch((e: Error) => setError(e.message));
                        }}
                      >
                        oeffnen
                      </button>
                    ) : (
                      <span className="muted">–</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
